// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { Link } from '@mui/material';
import Linkify from 'linkify-react';
import { IntermediateRepresentation, Opts } from 'linkifyjs';
import { MouseEvent, PropsWithChildren } from 'react';

// Only these protocols are turned into clickable links. Anything else linkify
// emits (such as "ftp:" or "file:", or a scheme registered elsewhere via
// linkify's global registry) is rendered as plain text instead.
const ALLOWED_PROTOCOLS = ['http:', 'https:', 'mailto:'];

function getProtocol(href: string): string | null {
  try {
    return new URL(href).protocol;
  } catch {
    return null;
  }
}

// Render any detected URL or email address as a link. Web links open in a new
// tab. `defaultProtocol` lets schemeless URLs such as "www.google.com" resolve
// to a valid "https://www.google.com" href.
const linkifyOptions: Opts = {
  defaultProtocol: 'https',
  render: ({ attributes, content }: IntermediateRepresentation) => {
    const href = typeof attributes.href === 'string' ? attributes.href : '';
    const protocol = getProtocol(href);

    // Leave anything outside the allowlist as plain text rather than a link.
    // A fragment keeps the text inline without an extra wrapping element.
    if (protocol === null || !ALLOWED_PROTOCOLS.includes(protocol)) {
      return <>{content}</>;
    }

    // Only open web links in a new tab. A "mailto:" link with target="_blank"
    // hands off to the mail handler but leaves behind a blank tab in Chrome.
    const isWebLink = protocol === 'http:' || protocol === 'https:';

    return (
      <Link
        href={href}
        target={isWebLink ? '_blank' : undefined}
        rel={isWebLink ? 'noopener noreferrer' : undefined}
        // Stop the click from reaching an enclosing handler, such as an
        // editable control that opens an editor when its text is clicked.
        onClick={(event: MouseEvent<HTMLAnchorElement>) =>
          event.stopPropagation()
        }
      >
        {content}
      </Link>
    );
  },
};

/**
 * Renders user-entered free text, turning any URLs or email addresses into
 * links that open in a new tab. Wraps the text in an inline span so it can be
 * embedded inside a Typography while inheriting its styles, such as
 * `whiteSpace: 'pre-wrap'`.
 */
export function LinkifiedText(props: PropsWithChildren) {
  return (
    <Linkify as="span" options={linkifyOptions}>
      {props.children}
    </Linkify>
  );
}
